//* Importing modules
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuestions } from "../../../hooks/questions/useQuestions";
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";
import { useQuestionTag } from "../../../hooks/questionsTags/useQuestionTag";
import { useAttributions } from "../../../hooks/demand/attributions/useAtributions";

//* Importing components
import {
  Form,
  InputGroup,
  Button,
  Table,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  ProgressBar,
  Fade,
} from "react-bootstrap";

//! Importing Jodit Editor
import JoditEditor from "jodit-react";

//? Importing icons
import { SaveRounded } from "@material-ui/icons";
import { FaPlusSquare } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { BsFillEyeFill } from "react-icons/bs";
import { RiFilePdfFill } from "react-icons/ri";


//todo Importing components
import CustomAlert from "../../../component/base/CustomAlert";
import { TagsSelector } from "../../../component/common/TagsSelector";
import { SetSubQuestionModal } from "../form/components/SubQuestionModal";
import QuestionModal from "../form/components/ModalQuestionExist";
import { ErrorMessages } from "../../../component/common/ErrorMessages";

//? Importing types
import {
  QuestionEducationLevel,
  QuestionDifficultyLevel,
  QuestionType,
} from "../../../hooks/questions/types/types";
import {
  ObjectiveAlternative,
  LocalSubQuestion,
  OldTag,
} from "../form/types/types";

//! Importing modules
import { defaultEditorConfig } from "../../../constants/configJodit";
import { useSanitizeInput } from "../../../constants/configJodit";

//todo Importing utuils
import { fixImageUrl } from "../../../utils/fixImageUrl";
import { checkFileType } from "../../../utils/questions/checkFileType";

//* Block of code
const customStyles = `
  .custom-progress-label {
    color: #333 !important;
    font-weight: bold;
  }
`;

//* Function to transform the status of the demand into a label
const getStatusLabel = (status: string) => {
  switch (status) {
    case "NotStarted":
      return '<span style="color: #e31313; font-weight: bold;">Não Iniciado</span>';
    case "InProgress":
      return '<span style="color: orange; font-weight: bold;">Em Progresso</span>';
    case "Finished":
      return '<span style="color: green; font-weight: bold;">Finalizado</span>';
    default:
      return status;
  }
};


/**
 * @fileoverview This component renders a form for creating or updating questions, specifically tailored for demand-based question creation.
 * It integrates with various hooks for managing disciplines, tags, questions, and attributions.
 * The form supports objective, dissertative, and set question types, with dynamic UI elements based on the selected type.
 * It also handles validation, error display, and saving of the question data.
 *
 * @requires react
 * @requires react-router-dom
 * @requires react-redux
 * @requires react-bootstrap
 * @requires react-fade-in
 * @requires @fortawesome/react-fontawesome
 * @requires jodit-react
 * @requires react-icons/ri
 * @requires react-icons/bs
 * @requires react-icons/md
 *
 * @requires ./hooks/useDisciplines
 * @requires ./hooks/useQuestionTag
 * @requires ./hooks/useQuestions
 * @requires ./hooks/useAttributions
 * @requires ./components/TagsSelector
 * @requires ./components/ErrorMessages
 * @requires ./components/CustomAlert
 * @requires ./components/SetSubQuestionModal
 * @requires ./components/QuestionModal
 * @requires ./constants/editorConfig
 * @requires ./constants/storageUrl
 * @requires ./utils/getStatusLabel
 * @requires ./hooks/useSanitizeInput
 *
 * @component
 * @name QuestionsDemandForm
 *
 * @example
 * // Usage within a route context:
 * <Route path="/questions/demand/:id/:idAttribute" element={<QuestionsDemandForm />} />
 *
 * @returns {JSX.Element} The rendered form for creating or updating questions.
 */
export function QuestionsDemandForm() {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, idAttribute } = useParams(); 
  const token = useSelector((state: any) => state.auth.token);

  // Hooks for managing disciplines, tags, questions, and attributions
  const { discipline } = useDisciplines(token);
  const { createRelations, deleteRelations } = useQuestionTag(token);
  const { createQuestion, isCreating, isUpdating } = useQuestions(token);
  const { getAttributionById, updateStatusAttribution } = useAttributions(
    token,
    idAttribute
  );

  // States for form fields and data
  const [disciplina, setDisciplina] = useState<string>("");
  const [nivelEscolaridade, setNivelEscolaridade] =
    useState<QuestionEducationLevel>("elementary");
  const [nivelDificuldade, setNivelDificuldade] =
    useState<QuestionDifficultyLevel>("easy");

  // Tags
  const [tags, setTags] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [oldTags, setOldTags] = useState<OldTag[]>([]);

  // Content for the question
  const [statement, setStatement] = useState<string>("");
  const [justification, setJustification] = useState<string>("");

  // States for error messages
  const [statementError, setStatementError] = useState<string>("");
  const [justificationError, setJustificationError] = useState<string>("");
  const [altError, setAltError] = useState<string>("");

  // Type of question (objective, dissertative, set)
  const [TypeQuestion, setTypeQuestion] = useState<QuestionType>("objective");

  // Alternativas para questões objetivas
  const [alternatives, setAlternatives] = useState<ObjectiveAlternative[]>([]);

  // Subquestões para tipo "set"
  const [subQuestions, setSubQuestions] = useState<LocalSubQuestion[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalQuestion, setShowModalQuestion] = useState<boolean>(false);
  const [editingSubQ, setEditingSubQ] = useState<LocalSubQuestion | null>(null);

  // Estados para erros gerais e flag de tentativa de salvar
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [attemptedSave, setAttemptedSave] = useState<boolean>(false);
  const clearErrorMessages = useCallback(() => setErrorMessages([]), []);

  // Estado para armazenar o payload (em caso de erro 409)
  const [payloadState, setPayloadState] = useState<any>(null);

  // Estado de loading para spinner
  const [loading, setLoading] = useState<boolean>(false);

  // Refs para os editores
  const statementEditorRef = useRef<any>(null);
  const justificationEditorRef = useRef<any>(null);

  // Estado para armazenar as informações da atribuição
  const [demandInfo, setDemandInfo] = useState<any>(null);

  const { sanitizeInput } = useSanitizeInput();

  // Carrega as informações da atribuição via idA
  useEffect(() => {
    async function fetchDemand() {
      if (idAttribute) {
        const attributionData = await getAttributionById(idAttribute);
        if (attributionData) {
          setDemandInfo(attributionData);
          setDisciplina(attributionData.discipline.id);
        }
      }
    }
    fetchDemand();
  }, [idAttribute, getAttributionById]);

  // Seleciona automaticamente o tipo de questão conforme pendências na atribuição
  useEffect(() => {
    if (demandInfo) {
      if (
        demandInfo.quantityObjective > 0 &&
        demandInfo.quantityObjectiveFinish < demandInfo.quantityObjective
      ) {
        setTypeQuestion("objective");
      } else if (
        demandInfo.quantityDissertative > 0 &&
        demandInfo.quantityDissertativeFinish < demandInfo.quantityDissertative
      ) {
        setTypeQuestion("dissertative");
      } else if (
        demandInfo.quantitySet > 0 &&
        demandInfo.quantitySetFinish < demandInfo.quantitySet
      ) {
        setTypeQuestion("set");
      } else {
        setTypeQuestion("objective");
      }
    }
  }, [demandInfo]);

  // Validação dos campos obrigatórios
  const validateFields = useCallback((): boolean => {
    const missing: string[] = [];
    if (!disciplina) missing.push("disciplina");
    if (!nivelEscolaridade) missing.push("nivelEscolaridade");
    if (!nivelDificuldade) missing.push("nivelDificuldade");
    if (!statement) missing.push("enunciado");
    if (TypeQuestion === "objective" && alternatives.length === 0)
      missing.push("alternativas");
    if (TypeQuestion === "set" && subQuestions.length === 0)
      missing.push("subQuestions");
    return missing.length === 0;
  }, [
    disciplina,
    nivelEscolaridade,
    nivelDificuldade,
    statement,
    TypeQuestion,
    alternatives,
    subQuestions,
  ]);

  useEffect(() => {
    setStatementError(statement ? "" : "Enunciado é obrigatório.");
  }, [statement]);

  useEffect(() => {
    if (TypeQuestion === "set") {
      setJustificationError("");
    } else {
      setJustificationError(
        justification
          ? ""
          : "justificativa é obrigatória para este tipo de questão."
      );
    }
  }, [justification, TypeQuestion]);

  useEffect(() => {
    if (TypeQuestion === "objective") {
      const correctCount = alternatives.filter((alt) => alt.isCorrect).length;
      setAltError(
        alternatives.length > 0 && correctCount !== 1
          ? "Exatamente uma alternativa deve ser marcada como correta."
          : ""
      );
    } else {
      setAltError("");
    }
  }, [alternatives, TypeQuestion]);

  const mapSubQuestionForPayload = useCallback((sq: LocalSubQuestion) => {
    const subQuestionPayload: any = {
      statement: sq.statement,
      answer_justification: sq.answerJustification,
      type: sq.type,
    };
    if (sq.id) subQuestionPayload.id = sq.id;
    if (sq.type === "objective" && sq.objectives) {
      subQuestionPayload.objectives = sq.objectives.map(
        (obj: any, index: number) => {
          const objPayload: any = {
            text: obj.text,
            is_correct: obj.isCorrect,
            order: index + 1,
          };
          if (obj.id) objPayload.id = obj.id;
          return objPayload;
        }
      );
    }
    return subQuestionPayload;
  }, []);

  const buildPayload = useCallback(() => {
    const payload: any = {
      discipline_id: disciplina,
      education_level: nivelEscolaridade,
      difficulty: nivelDificuldade,
    };

    if (TypeQuestion === "objective") {
      payload.sub_questions = [
        mapSubQuestionForPayload({
          localId: subQuestions[0]?.localId || crypto.randomUUID(),
          id: subQuestions[0]?.id,
          type: "objective",
          statement: statement,
          answerJustification: justification,
          objectives: alternatives,
        }),
      ];
    } else if (TypeQuestion === "dissertative") {
      payload.sub_questions = [
        mapSubQuestionForPayload({
          localId: subQuestions[0]?.localId || crypto.randomUUID(),
          id: subQuestions[0]?.id,
          type: "dissertative",
          statement: statement,
          answerJustification: justification,
        }),
      ];
    } else if (TypeQuestion === "set") {
      payload.statement = statement;
      payload.sub_questions = subQuestions.map(mapSubQuestionForPayload);
    }
    return payload;
  }, [
    disciplina,
    nivelEscolaridade,
    nivelDificuldade,
    statement,
    justification,
    TypeQuestion,
    subQuestions,
    alternatives,
    mapSubQuestionForPayload,
  ]);

  // Ao salvar a questão, atualiza a atribuição; a seção "Demanda Concluída" aparecerá se o status for Finished
  const handleSaveAll = useCallback(async () => {
    setAttemptedSave(true);
    if (!validateFields() || statementError || justificationError || altError) {
      setErrorMessages([
        "Existem campos obrigatórios não preenchidos ou com erros.",
      ]);
      return;
    }
    setLoading(true);
    try {
      const payload = buildPayload();
      setPayloadState(payload);
      // Cria a nova questão
      const newQuestion = await createQuestion(payload);
      const newQuestionId = newQuestion.id;
      if (newQuestionId) {
        const newTagIds = tags;
        const oldTagIds = oldTags.map((t) => t.id);
        const tagsToCreate = newTagIds
          .filter((tagId) => !oldTagIds.includes(tagId))
          .map((tagId) => ({
            question_id: newQuestionId,
            tag_id: tagId,
          }));
        const tagsToDelete = oldTags
          .filter((t) => !newTagIds.includes(t.id) && t.questionTagId)
          .map((t) => t.questionTagId as string);
        if (tagsToCreate.length > 0)
          await createRelations({ associations: tagsToCreate });
        if (tagsToDelete.length > 0) await deleteRelations(tagsToDelete);
      }
      // Atualiza os contadores e o status da atribuição
      if (demandInfo) {
        const updatedAttribution = await updateStatusAttribution({
          id: demandInfo.id,
          type: TypeQuestion,
        });
        setDemandInfo(updatedAttribution);
      }
    } catch (error: any) {
      console.error("Erro ao salvar questão:", error);
      setErrorMessages([error.message || "Erro ao salvar. Tente novamente."]);
    } finally {
      setLoading(false);
    }
  }, [
    validateFields,
    buildPayload,
    createQuestion,
    tags,
    oldTags,
    createRelations,
    deleteRelations,
    statementError,
    justificationError,
    altError,
    demandInfo,
    TypeQuestion,
    updateStatusAttribution,
  ]);

  const handleConfirmCreate = useCallback(async () => {
    try {
      if (!payloadState) {
        setErrorMessages(["Dados da questão não encontrados."]);
        return;
      }
      const newQuestion = await createQuestion(payloadState);
      const newQuestionId = newQuestion.id;
      if (tags.length > 0) {
        const associations = tags.map((tagId) => ({
          question_id: newQuestionId,
          tag_id: tagId,
        }));
        await createRelations({ associations });
      }
      navigate(-1);
    } catch (error: any) {
      console.error("Erro ao criar nova questão:", error);
      setErrorMessages(["Erro ao criar a nova questão. Tente novamente."]);
    } finally {
      setShowModalQuestion(false);
    }
  }, [payloadState, createQuestion, tags, createRelations, navigate]);

  const handleCancelModal = useCallback(() => {
    setShowModalQuestion(false);
  }, []);

  const handleAddAlternative = useCallback(() => {
    setAlternatives((prev) => [
      ...prev,
      { localId: crypto.randomUUID(), text: "", isCorrect: false },
    ]);
  }, []);

  const handleRemoveAlternative = useCallback((localId: string) => {
    setAlternatives((prev) => prev.filter((alt) => alt.localId !== localId));
  }, []);

  const handleOpenModal = useCallback(() => {
    setEditingSubQ(null);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleSaveSubQuestion = useCallback(
    (data: LocalSubQuestion) => {
      setSubQuestions((prev) =>
        editingSubQ
          ? prev.map((sq) =>
              sq.localId === editingSubQ.localId ? { ...sq, ...data } : sq
            )
          : [...prev, data]
      );
      setShowModal(false);
      setEditingSubQ(null);
    },
    [editingSubQ]
  );

  const handleEditSubQuestion = useCallback((subQ: LocalSubQuestion) => {
    setEditingSubQ(subQ);
    setShowModal(true);
  }, []);

  const handleDeleteSubQuestion = useCallback((localId: string) => {
    setSubQuestions((prev) => prev.filter((sq) => sq.localId !== localId));
  }, []);

  const indexToLetter = (index: number): string => {
    return String.fromCharCode(97 + index) + ")";
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Bloco de estilos para sobrescrever a cor do label da ProgressBar */}
      <style type="text/css">{customStyles}</style>
      <div className="row">
        <div className="col-12">
          <div className="py-5 px-3">
            {attemptedSave && errorMessages.length > 0 && (
              <div className="text-center mb-3">
                <ErrorMessages
                  messages={errorMessages}
                  clearErrors={clearErrorMessages}
                />
              </div>
            )}

            {/* Cartão moderno de informações da atribuição */}
            {demandInfo && (
              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div>
                      <h5 className="card-title">
                        Referente a demanda: {demandInfo.demand.name}
                      </h5>
                      {demandInfo.description && (
                        <p className="card-text">{demandInfo.description}</p>
                      )}
                      <p className="card-text">
                        <strong>Professor:</strong> {demandInfo.teacher.name}
                      </p>
                      <p className="card-text">
                        <strong>Disciplina:</strong>{" "}
                        {demandInfo.discipline.name}
                      </p>
                      {demandInfo.attachmentUrl && (
                        <div className="mt-2">
                          <strong>Anexo:</strong>
                          {(() => {
                            const attachment = demandInfo.attachmentUrl;
                            const finalLink = fixImageUrl(attachment);
                            const ext =
                              attachment.split(".").pop()?.toLowerCase() || "";

                            const fileType = checkFileType(ext);

                            if (fileType === "image") {
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginTop: "5px",
                                  }}
                                >
                                  <img
                                    src={finalLink}
                                    alt="Anexo"
                                    style={{
                                      maxWidth: "100px",
                                      maxHeight: "100px",
                                      borderRadius: "4px",
                                      border: "1px solid #ccc",
                                    }}
                                  />
                                  <a
                                    href={finalLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary btn-sm"
                                  >
                                    Download Imagem
                                  </a>
                                </div>
                              );
                            } else if (fileType === "pdf") {
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    marginTop: "5px",
                                  }}
                                >
                                  <RiFilePdfFill
                                    style={{
                                      fontSize: "2rem",
                                      color: "#d9534f",
                                    }}
                                  />
                                  <a
                                    href={finalLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary btn-sm"
                                  >
                                    Visualizar/Download PDF
                                  </a>
                                </div>
                              );
                            } else {
                              return (
                                <a
                                  href={finalLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-primary btn-sm"
                                  style={{ marginTop: "5px" }}
                                >
                                  Acessar Anexo
                                </a>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 mt-md-0 text-md-end">
                      <p className="card-text">
                        <strong>Status:</strong>{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: getStatusLabel(demandInfo.status),
                          }}
                        />
                      </p>
                      <div>
                        {demandInfo.quantityObjective > 0 && (
                          <div className="mb-2">
                            <small>Objetivas</small>
                            <ProgressBar
                              variant="success"
                              now={
                                (demandInfo.quantityObjectiveFinish /
                                  demandInfo.quantityObjective) *
                                100
                              }
                              label={
                                <span className="custom-progress-label">
                                  {demandInfo.quantityObjectiveFinish} de{" "}
                                  {demandInfo.quantityObjective}
                                </span>
                              }
                            />
                          </div>
                        )}
                        {demandInfo.quantityDissertative > 0 && (
                          <div className="mb-2">
                            <small>Dissertativas</small>
                            <ProgressBar
                              variant="warning"
                              now={
                                (demandInfo.quantityDissertativeFinish /
                                  demandInfo.quantityDissertative) *
                                100
                              }
                              label={
                                <span className="custom-progress-label">
                                  {demandInfo.quantityDissertativeFinish} de{" "}
                                  {demandInfo.quantityDissertative}
                                </span>
                              }
                            />
                          </div>
                        )}
                        {demandInfo.quantitySet > 0 && (
                          <div className="mb-2">
                            <small>Textos em Comum</small>
                            <ProgressBar
                              variant="success"
                              now={
                                (demandInfo.quantitySetFinish /
                                  demandInfo.quantitySet) *
                                100
                              }
                              label={
                                <span className="custom-progress-label">
                                  {demandInfo.quantitySetFinish} de{" "}
                                  {demandInfo.quantitySet}
                                </span>
                              }
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Se a demanda estiver concluída, exibe uma seção com animação suave */}
            {demandInfo && demandInfo.status === "Finished" ? (
              <Fade in={true} appear={true} timeout={500}>
                <div className="text-center my-4">
                  <h4 className="text-danger">Demanda Concluída</h4>
                  <p>
                    Não é possível criar novas questões, pois sua demanda foi
                    concluída.
                  </p>
                  <Button variant="primary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </div>
              </Fade>
            ) : (
              // Caso contrário, exibe o formulário para criação de questão
              <Form style={{ position: "relative" }}>
                {/* Seletor de Tipo de Questão */}
                <InputGroup className="mb-3">
                  <div className="flex fdcolumn flex1">
                    <Form.Label>Tipo de Questão</Form.Label>
                    <Form.Select
                      data-testid="question-type-selector"
                      value={TypeQuestion}
                      onChange={(e) =>
                        setTypeQuestion(e.target.value as QuestionType)
                      }
                    >
                      <option
                        value="objective"
                        disabled={
                          demandInfo &&
                          (demandInfo.quantityObjective === 0 ||
                            demandInfo.quantityObjectiveFinish ===
                              demandInfo.quantityObjective)
                        }
                      >
                        Objetiva
                      </option>
                      <option
                        value="dissertative"
                        disabled={
                          demandInfo &&
                          (demandInfo.quantityDissertative === 0 ||
                            demandInfo.quantityDissertativeFinish ===
                              demandInfo.quantityDissertative)
                        }
                      >
                        Dissertativa
                      </option>
                      <option
                        value="set"
                        disabled={
                          demandInfo &&
                          (demandInfo.quantitySet === 0 ||
                            demandInfo.quantitySetFinish ===
                              demandInfo.quantitySet)
                        }
                      >
                        Textos em Comum
                      </option>
                    </Form.Select>
                  </div>
                </InputGroup>

                {/* Disciplina definida pela atribuição */}
                <InputGroup className="mb-3">
                  <div className="flex fdcolumn flex1">
                    <Form.Label>
                      Disciplina <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      data-testid="discipline-selector"
                      value={disciplina}
                      disabled
                    >
                      <option value="">Selecione uma Disciplina</option>
                      {discipline.map((disc: any) => (
                        <option key={disc.id} value={disc.id}>
                          {disc.name}
                        </option>
                      ))}
                    </Form.Select>
                    {attemptedSave && !disciplina && (
                      <CustomAlert variant="danger">
                        Campo obrigatório
                      </CustomAlert>
                    )}
                  </div>
                </InputGroup>

                {/* Nível de Escolaridade e Dificuldade */}
                <div className="row">
                  <InputGroup className="col-lg mb-3">
                    <div className="flex fdcolumn flex1">
                      <Form.Label>
                        Nível de Escolaridade{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Select
                        data-testid="education-level-selector"
                        value={nivelEscolaridade}
                        onChange={(e) =>
                          setNivelEscolaridade(
                            e.target.value as QuestionEducationLevel
                          )
                        }
                      >
                        <option value="elementary">Fundamental</option>
                        <option value="high_school">Médio</option>
                        <option value="college">Superior</option>
                      </Form.Select>
                      {attemptedSave && !nivelEscolaridade && (
                        <CustomAlert variant="danger">
                          Campo obrigatório
                        </CustomAlert>
                      )}
                    </div>
                  </InputGroup>
                  <InputGroup className="col-lg mb-3">
                    <div className="flex fdcolumn flex1">
                      <Form.Label>
                        Nível de Dificuldade{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Select
                        data-testid="difficulty-level-selector"
                        value={nivelDificuldade}
                        onChange={(e) =>
                          setNivelDificuldade(
                            e.target.value as QuestionDifficultyLevel
                          )
                        }
                      >
                        <option value="easy">Fácil</option>
                        <option value="medium">Médio</option>
                        <option value="hard">Difícil</option>
                      </Form.Select>
                      {attemptedSave && !nivelDificuldade && (
                        <CustomAlert variant="danger">
                          Campo obrigatório
                        </CustomAlert>
                      )}
                    </div>
                  </InputGroup>
                </div>

                {/* Tags */}
                <InputGroup className="mb-3">
                  <div className="flex fdcolumn flex1">
                    <TagsSelector
                      selectedTagIds={tags}
                      onChange={(selectedTagIds) => setTags(selectedTagIds)}
                    />
                  </div>
                </InputGroup>

                {/* Editores de Enunciado e justification */}
                {disciplina ? (
                  <>
                    <InputGroup className="mb-3">
                      <Form.Label>
                        {TypeQuestion === "set"
                          ? "Descrição do Conjunto"
                          : "Enunciado"}{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <div className="w-100">
                        <JoditEditor
                          ref={statementEditorRef}
                          value={statement || ""}
                          config={defaultEditorConfig}
                          onBlur={(content: string) => setStatement(content)}
                          data-testid="jodit-editor"
                        />
                      </div>
                      {attemptedSave && statementError && (
                        <Form.Text className="text-danger">
                          {statementError}
                        </Form.Text>
                      )}
                    </InputGroup>
                    {TypeQuestion !== "set" && (
                      <InputGroup className="mb-3">
                        <Form.Label>Justificativa</Form.Label>
                        <div className="w-100">
                          <JoditEditor
                            ref={justificationEditorRef}
                            value={justification || ""}
                            config={defaultEditorConfig}
                            onBlur={(content) => setJustification(content)}
                            data-testid="jodit-editor-justification"
                          />
                        </div>
                        {attemptedSave && justificationError && (
                          <Form.Text className="text-danger">
                            {justificationError}
                          </Form.Text>
                        )}
                      </InputGroup>
                    )}
                  </>
                ) : (
                  <div className="alert alert-warning" role="alert">
                    Selecione uma disciplina para continuar.
                  </div>
                )}

                {/* Alternativas para questões objetivas */}
                {disciplina && TypeQuestion === "objective" && (
                  <Card
                    className="mb-3"
                    style={{ border: "1px solid #ccc", padding: 10 }}
                  >
                    <Card.Header>
                      <h5 className="mb-0">Alternativas</h5>
                    </Card.Header>
                    <Card.Body>
                      <Container>
                        <Row className="align-items-center mb-3">
                          <Col xs={6}>
                            <Form.Select defaultValue="1">
                              <option value="1">1 Alternativa</option>
                            </Form.Select>
                          </Col>
                          <Col
                            xs="auto"
                            className="d-flex justify-content-center"
                          >
                            <Button
                              variant="success"
                              data-testid="add-alternative-button"
                              onClick={handleAddAlternative}
                            >
                              <FaPlusSquare style={{ marginRight: 5 }} />
                              Adicionar Alternativa
                            </Button>
                          </Col>
                        </Row>
                        {alternatives.map((alt: any, index: any) => (
                          <Card key={alt.localId} className="mb-2">
                            <Card.Body>
                              <Row>
                                <Col xs={12} className="mb-2">
                                  <strong>{indexToLetter(index)}</strong>
                                </Col>
                                <Col xs={12}>
                                  <JoditEditor
                                    value={alt.text || ""}
                                    config={defaultEditorConfig}
                                    onBlur={(content) =>
                                      setAlternatives((prev) =>
                                        prev.map((a) =>
                                          a.localId === alt.localId
                                            ? { ...a, text: content }
                                            : a
                                        )
                                      )
                                    }
                                    data-testid="QuestionsDemandForm"
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  className="d-flex justify-content-between align-items-center mt-2"
                                >
                                  <Form.Check
                                    type="checkbox"
                                    label="Correta?"
                                    checked={alt.isCorrect}
                                    onChange={(e) =>
                                      setAlternatives((prev) =>
                                        prev.map((a) =>
                                          a.localId === alt.localId
                                            ? {
                                                ...a,
                                                isCorrect: e.target.checked,
                                              }
                                            : { ...a, isCorrect: false }
                                        )
                                      )
                                    }
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() =>
                                      handleRemoveAlternative(alt.localId)
                                    }
                                  >
                                    Remover
                                  </Button>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        ))}
                        {attemptedSave && alternatives.length === 0 && (
                          <div className="text-center">
                            <CustomAlert variant="danger">
                              Adicione pelo menos 1 alternativa
                            </CustomAlert>
                          </div>
                        )}
                        {altError && (
                          <div className="text-center mt-2">
                            <Form.Text className="text-danger">
                              {altError}
                            </Form.Text>
                          </div>
                        )}
                      </Container>
                    </Card.Body>
                  </Card>
                )}

                {/* Subquestões para tipo "set" */}
                {disciplina && TypeQuestion === "set" && (
                  <div className="p-3 border rounded bg-light mb-3">
                    <h5 className="fw-semibold">SubQuestões</h5>
                    <p className="text-muted">
                      Cada subquestão pode ser do tipo objetiva ou dissertativa.
                    </p>
                    <Button
                      variant="primary"
                      data-testid="add-subquestion-button"
                      onClick={handleOpenModal}
                      className="mb-3"
                    >
                      <FaPlusSquare className="me-2" />
                      Adicionar SubQuestão
                    </Button>
                    {subQuestions.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="rounded shadow-sm">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th>Tipo</th>
                              <th>Enunciado</th>
                              <th
                                style={{ width: "120px" }}
                                className="text-center"
                              >
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {subQuestions.map((sq) => (
                              <tr key={sq.localId} className="align-middle">
                                <td>
                                  <span
                                    className={`badge ${
                                      sq.type === "objective"
                                        ? "bg-success"
                                        : "bg-warning"
                                    } text-white`}
                                  >
                                    {sq.type === "objective"
                                      ? "Objetiva"
                                      : "Dissertativa"}
                                  </span>
                                </td>
                                <td>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeInput(sq.statement),
                                    }}
                                  />
                                </td>
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={() => handleEditSubQuestion(sq)}
                                      style={{ width: "32px", height: "32px" }}
                                    >
                                      <BsFillEyeFill />
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        handleDeleteSubQuestion(sq.localId)
                                      }
                                      style={{ width: "32px", height: "32px" }}
                                    >
                                      <MdDelete />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center text-muted mt-3">
                        <p className="mb-0">Nenhuma subquestão adicionada.</p>
                      </div>
                    )}
                    {attemptedSave && subQuestions.length === 0 && (
                      <CustomAlert variant="danger">
                        Adicione pelo menos 1 subquestão
                      </CustomAlert>
                    )}
                  </div>
                )}

                <div className="w-100 d-flex justify-content-between mt-3">
                  <Button
                    variant="secondary"
                    data-testid="back-button"
                    onClick={() => navigate(-1)}
                  >
                    Voltar
                  </Button>
                  <Button
                    variant="success"
                    data-testid="save-button"
                    onClick={handleSaveAll}
                    disabled={isCreating || isUpdating}
                  >
                    <SaveRounded style={{ marginRight: 5 }} />
                    {isCreating || isUpdating ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>

        {/* Modal de SubQuestão */}
        <SetSubQuestionModal
          show={showModal}
          onHide={handleCloseModal}
          onSave={handleSaveSubQuestion}
          editingData={editingSubQ}
        />

        {/* Modal de Questão Existente (erro 409) */}
        <QuestionModal
          show={showModalQuestion}
          onConfirm={handleConfirmCreate}
          onCancel={handleCancelModal}
        />
      </div>
    </>
  );
}

export default QuestionsDemandForm;
